/**
 * Markdown 预览组件
 * 渲染 Markdown 为 HTML 并显示，使用 Notion 风格
 */
import { useEffect, useRef, useState, useImperativeHandle, forwardRef, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { renderMarkdownFull } from '../../features/markdown/services/markdownRenderer'
import { useDebounce } from '../../hooks/useDebounce'
import { useAppStore } from '../../store/useAppStore'
import { usePreviewActions } from './hooks/usePreviewActions'
import { useECharts } from './hooks/useECharts'
import { exportToCSV, exportToExcel } from '../../features/export/services/dataExporter'
import * as echarts from 'echarts'

export interface MarkdownPreviewRef {
  getElement: () => HTMLDivElement | null
}

interface MarkdownPreviewProps {
  content?: string
  className?: string
  debounceDelay?: number
  enableRealtime?: boolean
  onContentChange?: (newContent: string) => void
}

/**
 * 简单的XSS防护函数
 * 移除潜在的script标签和事件处理器属性
 */
const sanitizeHtml = (html: string): string => {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
    // 移除过于激进的 data: 替换，它会破坏内联图片和某些 SVG 属性
    // .replace(/data:/gi, '')
}

const MarkdownPreviewInner = forwardRef<MarkdownPreviewRef, MarkdownPreviewProps>(({
  content = '',
  className = '',
  debounceDelay = 300,
  enableRealtime: _enableRealtime = true
}, ref) => {
  const { t } = useTranslation()
  const previewRef = useRef<HTMLDivElement>(null)
  const [renderedContent, setRenderedContent] = useState<string>('')
  const [isRendering, setIsRendering] = useState(false)
  // 记录上次渲染的内容，避免不必要的 DOM 更新
  const lastRenderedRef = useRef<string>('')
  
  const openViewer = useAppStore(state => state.openViewer)
  const theme = useAppStore(state => state.theme)
  const setCodeEditModal = useAppStore(state => state.setCodeEditModal)

  // 使用useMemo缓存isDark计算结果，避免每次渲染都重新计算
  const isDark = useMemo(() => {
    return theme === 'dark' || (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  }, [theme])

  const isFullWidth = useAppStore(state => state.isFullWidth)

  const { handleDownload, handleCopy } = usePreviewActions()
  useECharts(renderedContent, isDark, previewRef)

  // 暴露 DOM 元素给父组件
  useImperativeHandle(ref, () => ({
    getElement: () => previewRef.current
  }))

  const debouncedContent = useDebounce(content, debounceDelay)

  // 渲染 Markdown 内容
  useEffect(() => {
    if (!debouncedContent) {
      setRenderedContent('')
      return
    }

    setIsRendering(true)
    const renderFrame = requestAnimationFrame(async () => {
      try {
        const html = await renderMarkdownFull(debouncedContent, isDark)
        setRenderedContent(html)
      } catch (error) {
        console.error('Preview render failed:', error)
      } finally {
        setIsRendering(false)
      }
    })

    return () => cancelAnimationFrame(renderFrame)
  }, [debouncedContent, isDark])

  // 手动更新 DOM，只有当 renderedContent 真正变化时才更新
  // 这样可以避免 isFullWidth 等状态变化时覆盖 ECharts 初始化的 DOM
  useEffect(() => {
    if (previewRef.current && renderedContent !== lastRenderedRef.current) {
      previewRef.current.innerHTML = sanitizeHtml(renderedContent)
      lastRenderedRef.current = renderedContent
    }
  }, [renderedContent])

  // 处理全局点击以关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      document.querySelectorAll('.preview-dropdown-wrapper.is-open').forEach(menu => {
        if (!menu.contains(e.target as Node)) {
          menu.classList.remove('is-open')
        }
      })
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 处理预览区域的点击事件（事件委托）
  const handlePreviewClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    
    // 1. 处理下拉菜单项点击
    const dropDownItem = target.closest('.preview-dropdown-item')
    if (dropDownItem) {
      e.preventDefault()
      e.stopPropagation()
      
      const format = dropDownItem.getAttribute('data-format')
      const action = dropDownItem.getAttribute('data-action')
      const wrapper = dropDownItem.closest('.copy-button-wrapper')
      const container = (wrapper?.querySelector('.mermaid-container, .echarts-container') as HTMLElement) || (wrapper?.querySelector('table[data-raw-markdown]') as HTMLElement)
      const tableElement = wrapper?.querySelector('table[data-raw-markdown]') as HTMLElement

      if (format === 'excel' || format === 'csv') {
        if (tableElement) {
          const rawMarkdown = tableElement.getAttribute('data-raw-markdown')
          if (rawMarkdown) {
            try {
              const decoded = decodeURIComponent(atob(rawMarkdown))
              if (format === 'excel') {
                exportToExcel(decoded, { format: 'excel', filename: 'table_export' })
              } else {
                exportToCSV(decoded, { format: 'csv', filename: 'table_export' })
              }
            } catch (err) {
              console.error('Failed to export table:', err)
            }
          }
        }
        dropDownItem.closest('.preview-dropdown-wrapper')?.classList.remove('is-open')
        return
      }

      if (action === 'internal-edit' && container) {
        const rawMarkdown = container.getAttribute('data-raw-markdown')
        if (rawMarkdown) {
          try {
            const decoded = decodeURIComponent(atob(rawMarkdown))
            let language = 'markdown'
            let code = decoded
            
            if (container.classList.contains('mermaid-container')) {
              const match = decoded.match(/```mermaid\n?([\s\S]*?)```/)
              if (match) {
                language = 'mermaid'
                code = match[1].trim()
              }
            } else if (container.classList.contains('echarts-container')) {
              const match = decoded.match(/```echarts\n?([\s\S]*?)```/)
              if (match) {
                language = 'echarts'
                code = match[1].trim()
              }
            }
            
            setCodeEditModal({
              isOpen: true,
              code,
              rawMarkdown,
              language
            })
          } catch (err) {
            console.error('Failed to decode code:', err)
          }
        }
      } else if (action === 'live-edit' && container) {
        const rawMarkdown = container.getAttribute('data-raw-markdown')
        if (rawMarkdown) {
          try {
            const decoded = decodeURIComponent(atob(rawMarkdown))
            
            if (container.classList.contains('echarts-container')) {
              window.open('https://echarts.apache.org/examples/zh/editor.html', '_blank')
            } else {
              const code = decoded.replace(/```mermaid\n?([\s\S]*?)```/, '$1').trim()
              const state = {
                code,
                mermaid: { theme: isDark ? 'dark' : 'default' },
                autoSync: true,
                updateDiagram: true
              }
              const json = JSON.stringify(state)
              const base64 = btoa(unescape(encodeURIComponent(json)))
              window.open(`https://mermaid.live/edit#base64:${base64}`, '_blank')
            }
          } catch (err) {
            console.error('Failed to open online editor:', err)
          }
        }
      } else if (format && container) {
        handleDownload(container, format as 'svg' | 'png' | 'jpeg')
      }
      
      dropDownItem.closest('.preview-dropdown-wrapper')?.classList.remove('is-open')
      return
    }

    // 2. 处理动作按钮点击
    const actionButton = target.closest('[data-action]') as HTMLElement
    if (!actionButton) return

    e.preventDefault()
    e.stopPropagation()

    const action = actionButton.getAttribute('data-action')
    const wrapper = actionButton.closest('.copy-button-wrapper')
    const el = wrapper?.querySelector('pre, table, .mermaid-container, .echarts-container') as HTMLElement

    if (action === 'download-menu' || action === 'edit-menu') {
      actionButton.closest('.preview-dropdown-wrapper')?.classList.toggle('is-open')
    } else if (action === 'internal-edit') {
      // 检查是否是普通代码块或表格的编辑
      // 1. 查找最近的带 data-raw-markdown 的元素 (pre 或 table)
      const targetElement = wrapper?.querySelector('[data-raw-markdown]') as HTMLElement
      
      if (targetElement) {
        const rawMarkdown = targetElement.getAttribute('data-raw-markdown')
        if (rawMarkdown) {
          try {
            const decoded = decodeURIComponent(atob(rawMarkdown))
            let language = 'markdown'
            let code = decoded

            // 如果是代码块，尝试提取语言
            if (targetElement.tagName.toLowerCase() === 'pre') {
               const match = decoded.match(/```(\w*)\n?([\s\S]*?)```/)
               if (match) {
                 language = match[1] || 'plaintext'
                 code = match[2].trim()
               }
            } else if (targetElement.classList.contains('echarts-container')) {
              language = 'echarts'
              const match = decoded.match(/```echarts\n?([\s\S]*?)```/)
              if (match) {
                code = match[1].trim()
              }
            } else if (targetElement.tagName.toLowerCase() === 'table') {
               // 表格使用渲染预览模式
               language = 'markdown-render'
               code = decoded.trim()
             }

            setCodeEditModal({
              isOpen: true,
              code,
              rawMarkdown,
              language
            })
          } catch (err) {
            console.error('Failed to decode content:', err)
          }
        }
      }
    } else if (action === 'live-edit') {
      const rawMarkdown = el.getAttribute('data-raw-markdown') || el.closest('[data-raw-markdown]')?.getAttribute('data-raw-markdown')
      if (rawMarkdown) {
        try {
          const decoded = decodeURIComponent(atob(rawMarkdown))
          
          if (el.classList.contains('echarts-container')) {
            window.open('https://echarts.apache.org/examples/zh/editor.html', '_blank')
          } else {
            const code = decoded.replace(/```mermaid\n?([\s\S]*?)```/, '$1').trim()
            
            const state = {
              code,
              mermaid: { theme: isDark ? 'dark' : 'default' },
              autoSync: true,
              updateDiagram: true
            }
            
            const json = JSON.stringify(state)
            const base64 = btoa(unescape(encodeURIComponent(json)))
            window.open(`https://mermaid.live/edit#base64:${base64}`, '_blank')
          }
        } catch (err) {
          console.error('Failed to open online editor:', err)
        }
      }
    } else if (action === 'copy' && el) {
      handleCopy(actionButton, el)
    } else if (action === 'zoom' && el) {
        let imageUrl = ''
        if (el.classList.contains('mermaid-container')) {
          const svg = el.querySelector('svg')
          if (svg) {
            try {
              const clonedSvg = svg.cloneNode(true) as SVGSVGElement
              clonedSvg.setAttribute('width', '2000')
              // 根据当前主题设置背景色
              clonedSvg.style.backgroundColor = isDark ? '#1a1a1a' : '#ffffff'
              
              // 提取并注入样式
              let styleText = Array.from(document.querySelectorAll('style[id^="mermaid-"]'))
                .map(s => s.textContent)
                .join('')
              
              // 如果 querySelector 没有找到，尝试从 styleSheets 提取 (适配生产环境)
              if (!styleText) {
                Array.from(document.styleSheets).forEach(sheet => {
                  try {
                    Array.from(sheet.cssRules).forEach(rule => {
                      if (rule.cssText.includes('mermaid')) {
                        styleText += rule.cssText
                      }
                    })
                  } catch (e) {}
                })
              }

              if (styleText) {
                const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style')
                styleEl.textContent = styleText
                clonedSvg.prepend(styleEl)
              }
              
              const svgData = new XMLSerializer().serializeToString(clonedSvg)
              // 使用更现代、可靠的 base64 编码方式处理 UTF-8
              const utf8Bytes = new TextEncoder().encode(svgData)
              const base64 = btoa(String.fromCharCode(...utf8Bytes))
              imageUrl = `data:image/svg+xml;base64,${base64}`
            } catch (err) {
              console.error('Mermaid zoom failed:', err)
            }
          }
        } else if (el.classList.contains('echarts-container')) {
        const chart = echarts.getInstanceByDom(el)
        imageUrl = chart?.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: isDark ? '#1a1a1a' : '#fff' }) || ''
      }
      if (imageUrl) openViewer(imageUrl)
    }
  }, [handleDownload, handleCopy, openViewer, isDark, setCodeEditModal])

  return (
    <div className="relative h-full flex flex-col">
      {/* 渲染状态指示器 - 仅在渲染时显示 */}
      {isRendering && (
        <div className="absolute top-2 right-2 z-10">
          <div className="flex items-center space-x-1 text-xs text-secondary bg-primary px-2 py-1 rounded border border-light">
            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>渲染中...</span>
          </div>
        </div>
      )}

      {/* 预览内容 - 使用 useEffect 手动更新 DOM，避免 isFullWidth 变化时覆盖 ECharts */}
      <div
        ref={previewRef}
        className={`preview-content markdown-body ${isFullWidth ? '!max-w-none' : ''} flex-1 overflow-auto ${className}`}
        onClick={handlePreviewClick}
      />

      {/* 空状态 */}
      {!renderedContent && !isRendering && (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{t('preview.emptyState') || '暂无内容预览'}</p>
          </div>
        </div>
      )}
    </div>
  )
})

MarkdownPreviewInner.displayName = 'MarkdownPreviewInner'
export const MarkdownPreview = memo(MarkdownPreviewInner)
